import { useMemo, useState, useCallback, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Markdown from 'react-markdown';
import { HiMagnifyingGlass, HiSparkles } from 'react-icons/hi2';
import type { SearchDocumentsResult } from '@/types';
import SearchResultCard from './SearchResultCard';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import {
  EmptyState,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateDescription,
  StreamingIndicator,
  StreamingDot,
  ResultsWrapper,
  AnswerBox,
  AnswerHeader,
  AnswerContent,
  AnswerCursor,
  ResultsHeader,
  ResultsCount,
} from './SearchResults.styled';

interface SearchResultsProps {
  data: SearchDocumentsResult | undefined;
  isLoading: boolean;
  hasSearched: boolean;
  answer?: string;
  isStreaming?: boolean;
}

export default function SearchResults({
  data,
  isLoading,
  hasSearched,
  answer,
  isStreaming,
}: SearchResultsProps) {
  const { t } = useTranslation();
  const [highlightedDocId, setHighlightedDocId] = useState<string | null>(null);
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Extract cited document IDs from raw answer (updates as tokens stream in)
  const citedDocumentIds = useMemo(() => {
    if (!answer) return new Set<string>();
    const ids = new Set<string>();
    const regex = /\[\[([^|]+)\|[^|]+\|[^\]]*\]\]/g;
    let match;
    while ((match = regex.exec(answer)) !== null) {
      ids.add(match[1]);
    }
    return ids;
  }, [answer]);

  // Replace [[documentId|fileName|pageNumber]] citations with markdown links
  const processedAnswer = useMemo(() => {
    if (!answer) return '';
    return answer.replace(
      /\[\[([^|]+)\|([^|]+)\|([^\]]*)\]\]/g,
      (_match, docId, fileName, page) => {
        const label = page ? `${fileName}, str. ${page}` : fileName;
        return `[${label}](/documents/${docId})`;
      },
    );
  }, [answer]);

  const scrollToCard = useCallback((docId: string) => {
    const el = document.getElementById(`search-card-${docId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      clearTimeout(highlightTimerRef.current);
      setHighlightedDocId(docId);
      highlightTimerRef.current = setTimeout(() => setHighlightedDocId(null), 1500);
    }
  }, []);

  if (isLoading && !data) {
    return (
      <div>
        <StreamingIndicator className="mb-4">
          <StreamingDot />
          {t('search.searchingDocuments')}
        </StreamingIndicator>
        <LoadingSpinner />
      </div>
    );
  }

  if (!hasSearched) {
    return null;
  }

  if (!data?.results.length) {
    return (
      <EmptyState>
        <EmptyStateIcon>
          <HiMagnifyingGlass />
        </EmptyStateIcon>
        <EmptyStateTitle>{t('search.noResults.title')}</EmptyStateTitle>
        <EmptyStateDescription>
          {t('search.noResults.description')}
        </EmptyStateDescription>
      </EmptyState>
    );
  }

  return (
    <ResultsWrapper>
      {data.llmEnabled && (answer || isStreaming) && (
        <AnswerBox>
          <AnswerHeader>
            <HiSparkles size="1.125rem" />
            <span>{t('search.aiAnswer')}</span>
          </AnswerHeader>
          <AnswerContent>
            <Markdown
              components={{
                a: ({ href, children }) => {
                  if (href?.startsWith('/documents/')) {
                    const docId = href.replace('/documents/', '');
                    return (
                      <a
                        href={href}
                        onClick={(e) => {
                          e.preventDefault();
                          scrollToCard(docId);
                        }}
                      >
                        {children}
                      </a>
                    );
                  }
                  return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
                },
              }}
            >
              {processedAnswer}
            </Markdown>
            {isStreaming && <AnswerCursor>|</AnswerCursor>}
          </AnswerContent>
        </AnswerBox>
      )}

      <ResultsHeader>
        <ResultsCount>
          <Trans
            i18nKey="search.results"
            count={data.totalCount}
            values={{ count: data.totalCount }}
            components={{ strong: <strong /> }}
          />
        </ResultsCount>
      </ResultsHeader>
      {data.results.map((result) => (
        <SearchResultCard
          key={result.documentId}
          id={`search-card-${result.documentId}`}
          result={result}
          isCitedByAi={citedDocumentIds.has(result.documentId)}
          isHighlighted={highlightedDocId === result.documentId}
        />
      ))}
    </ResultsWrapper>
  );
}
